import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import classnames from 'classnames';
import useOnClickOutside from 'use-onclickoutside';
import { useIntl } from '@grandvisionhq/state';
import { isTouchDevice } from '../Util/is-touch-device.js';
import 'lodash/uniq';
import '@grandvisionhq/utils';
import '../MediaQuery/MediaQueryContext.js';
import '../Util/get-donation-foundation-icon.js';
import useCursor from './hooks/use-cursor.js';
import useIsOpenSuggestions from './hooks/use-is-open-suggestions.js';
import Input from './Input/Input.js';
import Suggestions from './Suggestions/Suggestions.js';

const assignCursorNumberToSuggestion = (suggestions, hasTopContent)=>suggestions.map((suggestion, index)=>swcHelpers.objectSpread({}, suggestion, {
            cursor: hasTopContent ? index + 1 : index
        })
    )
;
const Autocomplete = (_param)=>{
    var { className , disabled =false , handleClickOutside , isFocusedOnMount , isInvalid , isSuggestionSelected , isValid , labels , loading , minCountCharactersToSubmit =0 , name , onChange , onClear , onSubmit , onSuggestionClick , onSuggestionTopContentClick , setIsTouched , showClearButton , submitIcon , suggestions , suggestionTopContent , type ='text' , value , inputRef =/*#__PURE__*/ React.createRef() , inputDataT  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "className",
        "disabled",
        "handleClickOutside",
        "isFocusedOnMount",
        "isInvalid",
        "isSuggestionSelected",
        "isValid",
        "labels",
        "loading",
        "minCountCharactersToSubmit",
        "name",
        "onChange",
        "onClear",
        "onSubmit",
        "onSuggestionClick",
        "onSuggestionTopContentClick",
        "setIsTouched",
        "showClearButton",
        "submitIcon",
        "suggestions",
        "suggestionTopContent",
        "type",
        "value",
        "inputRef",
        "inputDataT"
    ]);
    const { getLabel  } = useIntl();
    const ref1 = React.useRef(null);
    const hasTopContent = Boolean(suggestionTopContent);
    const hasFocusedOnMount = React.useRef(false);
    const [isSuggestionsOpen, setIsSuggestionsOpen] = useIsOpenSuggestions(inputRef);
    const suggestionsWithCursor = React.useMemo(()=>assignCursorNumberToSuggestion(suggestions, hasTopContent)
    , [
        suggestions,
        hasTopContent
    ]);
    useOnClickOutside(ref1, ()=>{
        setIsSuggestionsOpen(false);
        handleClickOutside && handleClickOutside(value);
    });
    const { cursor , setCursor , setHovered  } = useCursor({
        ref: inputRef,
        suggestions: suggestionsWithCursor,
        hasTopContent,
        onSuggestionClick,
        onSuggestionTopContentClick,
        onSubmit,
        setIsSuggestionsOpen
    });
    const showSuggestions = !isSuggestionSelected ? value.length > minCountCharactersToSubmit || suggestions.length > 0 : false;
    const handleOnClick = React.useCallback(()=>{
        setIsSuggestionsOpen(showSuggestions);
    }, [
        showSuggestions,
        setIsSuggestionsOpen
    ]);
    const handleOnChange = React.useCallback((event)=>{
        onChange(event);
    }, [
        onChange
    ]);
    const handleOnBlur = React.useCallback(()=>{
        if (cursor === (hasTopContent ? suggestions.length : suggestions.length - 1)) {
            setCursor(-1);
            setIsSuggestionsOpen(false);
        }
    }, [
        cursor,
        hasTopContent,
        suggestions.length,
        setCursor,
        setIsSuggestionsOpen
    ]);
    React.useEffect(()=>{
        /* do not focus the input field if mobile or tablet, because that will make
     * the virtual keyboard stay which hides the results or other part of the screen */ if (isFocusedOnMount && hasFocusedOnMount.current === false && isTouchDevice() === false) {
            var ref;
            (ref = inputRef.current) === null || ref === void 0 ? void 0 : ref.focus();
            hasFocusedOnMount.current = true;
        }
    }, [
        inputRef,
        isFocusedOnMount
    ]);
    React.useEffect(()=>{
        setIsSuggestionsOpen(showSuggestions);
    // Only update isSuggestionsOpen when suggestions or isSuggestionSelected are changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isSuggestionSelected,
        suggestions
    ]);
    var _submitButtonScreenReaderHint;
    const screenReaderLabels = React.useMemo(()=>({
            clearButtonScreenReaderHint: getLabel('elements.autocomplete.clear.button'),
            submitButtonScreenReaderHint: (_submitButtonScreenReaderHint = labels.submitButtonScreenReaderHint) !== null && _submitButtonScreenReaderHint !== void 0 ? _submitButtonScreenReaderHint : getLabel('elements.autocomplete.search.button'),
            // noAvailableResultsScreenReaderHint can be set when, for example, the first option is set to Use My Location even when there are no results.
            noAvailableResultsScreenReaderHint: labels.noAvailableResultsScreenReaderHint,
            availableResultsScreenReaderHint: getLabel('elements.autocomplete.availableResultsScreenReaderHint'),
            inputScreenReaderHint: getLabel('elements.autocomplete.inputScreenReaderHint')
        })
    , [
        getLabel,
        labels.noAvailableResultsScreenReaderHint
    ]);
    return /*#__PURE__*/ React.createElement("div", swcHelpers.extends({
        ref: ref1
    }, rest, {
        className: classnames('autocomplete', className)
    }), /*#__PURE__*/ React.createElement(Input, {
        count: suggestionsWithCursor.length,
        cursor: cursor,
        disabled: disabled,
        isInvalid: isInvalid,
        isListSuggestionsOpen: isSuggestionsOpen,
        isValid: isValid,
        labels: screenReaderLabels,
        loading: loading,
        name: name,
        onBlur: setIsTouched,
        onChange: handleOnChange,
        onClear: onClear,
        onClick: handleOnClick,
        onSubmit: onSubmit,
        placeholder: labels.placeholder,
        ref: inputRef,
        showClearButton: showClearButton,
        submitIcon: submitIcon,
        type: type,
        value: value,
        "data-t": inputDataT
    }), isSuggestionsOpen && /*#__PURE__*/ React.createElement(Suggestions, {
        autocompleteName: name,
        cursor: cursor,
        noResults: labels.noResults,
        onBlur: handleOnBlur,
        onSuggestionClick: onSuggestionClick,
        onSuggestionTopContentClick: onSuggestionTopContentClick,
        setFocus: setCursor,
        setHovered: setHovered,
        suggestions: suggestionsWithCursor,
        suggestionTopContent: suggestionTopContent
    }));
};

export { Autocomplete as default };
//# sourceMappingURL=Autocomplete.js.map
